// "Where are the gaps?" table: postcodes as rows, keywords as columns,
// built from a marketOverview() result.
//   measure: "ads" (advertisers in the ads) | "local" (listings in the map pack)
//   sort: "fewest" | "most" | "az"
//   groupByTown: rows grouped under their town
// Returns { keywords, groups: [{ town, rows }], hasTowns, summary: { cells, emptyCells } }.
// Each row: { postcode, town, cells: [{ keyword, value, searches }], any: { value, searches } }.

export const GAP_KEYWORD_COLUMNS = 6;

// "Gaps by keyword" map: one area per postcode for one keyword (null = all
// keywords in the filters) and one channel ("ppc" | "local" | "seo" | "shopping").
// Returns { areas: [{ postcode, town, searches, value, channels }], summary: { postcodes, empty } }.
// value = different advertisers seen there; summary counts postcodes with searches.
export function keywordMap(result, { keyword = null, channel = "ppc" } = {}) {
  const none = { ppc: 0, local: 0, seo: 0, shopping: 0 };
  const areas = result.grid
    .filter(g => g.keyword === keyword)
    .map(g => {
      const channels = g.channels || none;
      return { postcode: g.postcode, town: g.town, searches: g.searches, value: channels[channel] || 0, channels };
    })
    .sort(byPostcode);
  const searched = areas.filter(a => a.searches > 0);
  return { areas, summary: { postcodes: searched.length, empty: searched.filter(a => a.value === 0).length } };
}

// Colour step for a map area: "notSearched", then 0 (none), 1, 2, 3, 4 (4-5), 5 (6+).
export function competitionLevel(value, searches) {
  if (searches === 0) return "notSearched";
  if (value >= 6) return 5;
  if (value >= 4) return 4;
  return value;
}
const UNKNOWN_TOWN = "Town not known";

const byPostcode = (a, b) => a.postcode.localeCompare(b.postcode, "en-GB", { numeric: true });

export function gapTable(result, { measure = "ads", sort = "fewest", groupByTown = false } = {}) {
  const keywords = result.gapSummary.keywordsBySearches.slice(0, GAP_KEYWORD_COLUMNS).map(k => k.keyword);
  const valueOf = g => ({ value: measure === "local" ? g.localListings : g.ppcAdvertisers, searches: g.searches });

  const rowsByPostcode = new Map();
  for (const g of result.grid) {
    if (!rowsByPostcode.has(g.postcode)) {
      rowsByPostcode.set(g.postcode, { postcode: g.postcode, town: g.town, cells: {}, any: { value: 0, searches: 0 } });
    }
    const row = rowsByPostcode.get(g.postcode);
    if (g.keyword === null) row.any = valueOf(g);
    else row.cells[g.keyword] = valueOf(g);
  }
  const rows = [...rowsByPostcode.values()].map(row => ({
    ...row,
    cells: keywords.map(keyword => ({ keyword, ...(row.cells[keyword] || { value: 0, searches: 0 }) })),
  }));

  const compare = sort === "az"
    ? byPostcode
    : sort === "most"
      ? (a, b) => b.any.value - a.any.value || byPostcode(a, b)
      : (a, b) => a.any.value - b.any.value || byPostcode(a, b);
  rows.sort(compare);

  // Keyword x postcode cells with completed searches, and how many show nobody.
  const counted = rows.flatMap(r => r.cells).filter(c => c.searches > 0);
  const summary = { cells: counted.length, emptyCells: counted.filter(c => c.value === 0).length };

  const hasTowns = rows.some(r => r.town);
  if (!groupByTown || !hasTowns) return { keywords, groups: [{ town: null, rows }], hasTowns, summary };

  const groups = new Map();
  for (const row of rows) {
    const town = row.town || UNKNOWN_TOWN;
    if (!groups.has(town)) groups.set(town, []);
    groups.get(town).push(row);
  }
  const ordered = [...groups.entries()]
    .sort(([a], [b]) => (a === UNKNOWN_TOWN) - (b === UNKNOWN_TOWN) || a.localeCompare(b))
    .map(([town, townRows]) => ({ town, rows: townRows }));
  return { keywords, groups: ordered, hasTowns, summary };
}
